import { t } from '@/i18n';
import type { Coordinate, Game, Square } from './types';

/**
 * Pure grid helpers. No Vue, no fetch — so they are cheap to test and the rules stay in one
 * place. The backend owns the authoritative versions; these exist so the UI can render and
 * preview without a round trip.
 */

export const GRID_SIZE = 10;

export interface GridCell extends Coordinate {
  /** Null when nobody has claimed this coordinate. */
  square: Square | null;
}

/** Stable key for a coordinate — used for `v-for` keys and for the release URL. */
export const squareKey = (col: number, row: number): string => `${col}-${row}`;

/**
 * Expands the claimed-squares list into all 100 cells, row-major so a 10-column CSS grid
 * renders them in the right visual order.
 */
export function buildGrid(squares: Square[]): GridCell[] {
  const claimed = new Map(squares.map((square) => [squareKey(square.col, square.row), square]));
  const cells: GridCell[] = [];

  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      cells.push({ col, row, square: claimed.get(squareKey(col, row)) ?? null });
    }
  }

  return cells;
}

/**
 * How full the grid is and how much of that was paid for — two numbers rather than one, because a
 * `DISTRIBUTE_FREE` deal fills the board without adding a cent to the pot. "20 of 100 sold" above
 * a visibly full grid is true and reads like a mistake.
 *
 * Collapses back to one number when nothing was given away, which is every game until a host
 * locks under that policy.
 */
export function gridSummary(taken: number, sold: number): string {
  const total = GRID_SIZE * GRID_SIZE;
  return taken === sold
    ? t('gridSummary.sold', { sold, total })
    : t('gridSummary.takenAndSold', { taken, sold, total });
}

const CURRENCY_SYMBOLS: Record<string, string> = { ZAR: 'R', USD: '$', GBP: '£', EUR: '€' };

/**
 * Amounts are whole currency units — a square costs R1, not R1.00. Rendering cents would imply a
 * precision this app does not have.
 *
 * **Never locale-aware, in any language.** `Intl.NumberFormat` would put a space in `R1 000` in
 * one language and a dot in another, and the pot has to read the same to everybody in one pool.
 */
export function formatMoney(amount: number, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency];
  return symbol ? `${symbol}${amount}` : `${currency} ${amount}`;
}

/**
 * Whether this game has money in it at all.
 *
 * A price of zero is a whole mode rather than an unset field: plenty of groups play for a shot or
 * for bragging rights, and there is no second setting saying so — the zero *is* the setting, which
 * is what stops a flag and a price from ever disagreeing. Everything money-shaped is hidden behind
 * this: the pot, the ledger, the paid column, the amount on a winner's sentence.
 *
 * False for a game that is not there yet, so a screen mid-load shows no money rather than R0.
 */
export const playsForMoney = (game: Pick<Game, 'pricePerSquare'> | null | undefined): boolean =>
  (game?.pricePerSquare ?? 0) > 0;
